import { useDispatch, useSelector } from "react-redux";
import { FindVehicleBrandsEntity, FindVehicleModelsEntity } from "./interfaces";
import { findBrandsByYearStart, findModelsByYearAndBrandStart } from "./slice";

import { VEHICLE_TYPE } from "Components/Features/CheckPrice/Products/RiskProducts/VehicleProducts/vehicleConstants";
import { ReduxState } from "Store/rootReducer";
import { isDefined } from "Utils/functions";
import { useEffect } from "react";

export const useVehicleBrandsByYear = (
  vehicleType: VEHICLE_TYPE,
  year?: string,
): FindVehicleBrandsEntity => {
  const dispatch = useDispatch();
  const brands = useSelector(
    (state: ReduxState) => state.findVehicleType.brands,
  );
  const boundedYear = getBoundedYear(year, vehicleType);
  const resultBrands = boundedYear && brands[boundedYear];

  useEffect(() => {
    if (boundedYear && !resultBrands) {
      dispatch(findBrandsByYearStart({ vehicleType, year: boundedYear }));
    }
  }, [boundedYear]);

  return (
    resultBrands || {
      current: null,
      isLoading: false,
      error: null,
    }
  );
};

export const useVehicleModelsByYearAndBrand = (
  vehicleType: VEHICLE_TYPE,
  year?: string,
  brand?: string,
): FindVehicleModelsEntity => {
  const dispatch = useDispatch();
  const models = useSelector(
    (state: ReduxState) => state.findVehicleType.models,
  );
  const boundedYear = getBoundedYear(year, vehicleType);
  const resultBrands = (boundedYear && models[boundedYear]) || {};
  const resultModels = brand && resultBrands[brand];

  useEffect(() => {
    if (brand && boundedYear && !resultModels) {
      dispatch(
        findModelsByYearAndBrandStart({
          vehicleType,
          brand,
          year: boundedYear,
        }),
      );
    }
  }, [brand, boundedYear]);

  return (
    resultModels || {
      current: null,
      isLoading: false,
      error: null,
    }
  );
};

const CAR_LOWER_BOUNDARY_YEAR = 1995;
const getBoundedYear = (
  year: string | undefined,
  vehicleType: VEHICLE_TYPE,
): string => {
  // invalid year string
  if (!isDefined(year) || !/^\d{4}$/.test(year)) {
    return "";
  }

  // only car has a fixed lower boundary as far as I know atm
  if (vehicleType !== VEHICLE_TYPE.CAR) {
    return year;
  }

  if (Number(year) >= CAR_LOWER_BOUNDARY_YEAR) {
    return year;
  }

  return `${CAR_LOWER_BOUNDARY_YEAR}`;
};
